import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TagIcon,
  DocumentIcon,
  DocumentArrowDownIcon,
  ArrowsRightLeftIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckIcon,
  XMarkIcon,
  ListBulletIcon,
  AdjustmentsHorizontalIcon,
  ChevronUpIcon,
  FunnelIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { attributeIconMap, type AttributeIconKey } from '../utils/attributeIcons';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { StatCard, StatGroup } from '../components/StatCard';
import StatementDetailModal from '../components/modals/StatementDetailModal';
import Combobox from '../components/Combobox';
import ConfirmationModal from '../components/ConfirmationModal';
import ExcelExportModal from '../components/ExcelExportModal';
import CSVImporter from '../components/CSVImporter';
import AddBookingModal from '../components/AddBookingModal';
import useFinanceDesignContext from '../hooks/useFinanceDesignContext';

interface Account {
  id: number | string;
  name: string;
  kontotyp?: string;
  startbetrag?: number;
  currentBalance?: number;
  erstellt_am?: string;
}

interface Statement {
  id: string;
  originalId: string;
  buchungstag: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number | null;
  konto_id: number;
  von_konto_id?: number;
  nach_konto_id?: number;
  kontotyp: string;
  documents: any[];
  categories: any[];
  tags?: string[];
  name_zahlungsbeteiligter?: string;
  cumulativeBalance?: number;
}

/**
 * Konto -> Icon + Farbton für die mobile Zeilenkachel.
 *
 * Die Icons kommen aus der zentralen `attributeIconMap` (Issue #9), damit
 * „Girokonto" hier dasselbe Symbol trägt wie überall sonst. Zugeordnet wird
 * über Stichworte im Kontonamen, weil die Konten frei benannt werden können.
 */
const KONTO_VISUALS: { match: RegExp; iconKey: AttributeIconKey; tile: string }[] = [
  { match: /giro|bank|konto$/i, iconKey: 'bankAccount', tile: 'bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-400' },
  { match: /gäste|gaeste|gast/i, iconKey: 'guest', tile: 'bg-success-50 text-success-600 dark:bg-success-900/25 dark:text-success-400' },
  { match: /spende/i, iconKey: 'donation', tile: 'bg-warning-50 text-warning-600 dark:bg-warning-900/25 dark:text-warning-400' },
  { match: /kantine|getränk|getraenk/i, iconKey: 'drinksAccount', tile: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { match: /rücklage|ruecklage|spar/i, iconKey: 'reserves', tile: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  { match: /portemonaie|portemonnaie|kasse|bar/i, iconKey: 'cashBox', tile: 'bg-primary-50 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400' },
];

const getKontoVisual = (name?: string) => {
  const hit = name ? KONTO_VISUALS.find((v) => v.match.test(name)) : undefined;
  const iconKey: AttributeIconKey = hit?.iconKey ?? 'bankAccount';
  return {
    Icon: attributeIconMap[iconKey].component,
    tile: hit?.tile ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
};

/** Belegstatus als Punkt + Text (Entwurf „Kontoauszuege Mobile"). */
const STATUS_META: Record<'red' | 'amber' | 'green' | 'gray', { label: string; dot: string; text: string }> = {
  red: { label: 'Offen', dot: 'bg-error-500', text: 'text-error-700 dark:text-error-400' },
  amber: { label: 'Teilweise', dot: 'bg-warning-500', text: 'text-warning-700 dark:text-warning-400' },
  green: { label: 'Vollständig', dot: 'bg-success-500', text: 'text-success-700 dark:text-success-400' },
  gray: { label: 'Umbuchung', dot: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-400' },
};

/** Filter-Chip der Filterkarte (Entwurf: Pille mit Punkt und Zähler). */
const FilterChip: React.FC<{
  label: React.ReactNode;
  active: boolean;
  dot?: string;
  count?: number;
  onClick: () => void;
}> = ({ label, active, dot, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-[34px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-sm font-semibold transition-colors ${
      active
        ? 'border-primary-600 bg-primary-600 text-white'
        : 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-200'
    }`}
  >
    {dot && <span className={`h-[7px] w-[7px] flex-none rounded-full ${active ? 'bg-white/80' : dot}`} />}
    <span>{label}</span>
    {count !== undefined && (
      <span className={`text-[11px] tabular-nums ${active ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'}`}>
        {count}
      </span>
    )}
  </button>
);

const AccountStatements: React.FC = () => {
  useFinanceDesignContext();
  const { user } = useContext(AuthContext);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [allStatements, setAllStatements] = useState<Statement[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const statementsPerPage = 20;
  const [statusFilter, setStatusFilter] = useState<'all' | 'red' | 'blue' | 'green'>('all');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  // Filterkarte startet überall zugeklappt; der Zähler in der Kopfzeile zeigt,
  // ob überhaupt gefiltert wird.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [availableBuchungTags, setAvailableBuchungTags] = useState<{ tag: string; count: number }[]>([]);

  // Selection mode states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [categoryMasters, setCategoryMasters] = useState<any[]>([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  
  // ConfirmationModal State
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  // Fetch all data once
  const fetchData = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. Fetch accounts
      const resAccounts = await apiClient.get('/api/finance/accounts');
      const accountsData = resAccounts.data || [];
      const enrichedAccounts = [{ id: 'all', name: 'Alle Konten' }, ...accountsData];
      setAccounts(enrichedAccounts);

      // 2. Fetch buchung tags (für Filter-Chips)
      apiClient.get('/api/finance/buchung-tags')
        .then(res => setAvailableBuchungTags(res.data || []))
        .catch(() => {});

      // 3. Fetch all statements
      const resStatements = await apiClient.get(`/api/finance/statements-list?account_id=all&limit=0&offset=0`);

      if (resStatements.data.success) {
        const statementsData = resStatements.data.statements || [];

        const allStatementsData: Statement[] = statementsData.map((stmt: any) => ({
          ...stmt,
          originalId: stmt.id,
          id: `${stmt.konto_id}-${stmt.id}`,
          konto_id: Number(stmt.konto_id),
          saldo: stmt.saldo != null ? Number(stmt.saldo) : null,
          von_konto_id: stmt.von_konto_id ? Number(stmt.von_konto_id) : undefined,
          nach_konto_id: stmt.nach_konto_id ? Number(stmt.nach_konto_id) : undefined,
        }));

        // Backend liefert bereits DESC sortiert
        setAllStatements(allStatementsData);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Fehler beim Laden der Kontoauszüge');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  // Selection mode: Kategorien laden
  const enterSelectionMode = useCallback(async () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
    try {
      const res = await apiClient.get('/api/finance/categories');
      setCategoryMasters(res.data || []);
    } catch (err) {
      console.error('Kategorien laden fehlgeschlagen:', err);
    }
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelection = useCallback((originalId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(originalId)) {
        next.delete(originalId);
      } else {
        next.add(originalId);
      }
      return next;
    });
  }, []);

  const sortedBulkCategories = useMemo(() => {
    return [...categoryMasters].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'de'));
  }, [categoryMasters]);

  // Keyboard navigation for bulk category dropdown
  const handleBulkCategoryAssign = useCallback(async (kategorieId: number) => {
    if (selectedIds.size === 0) return;
    setBulkAssigning(true);
    try {
      await apiClient.post('/api/finance/statements/bulk-categories', {
        statement_ids: Array.from(selectedIds),
        kategorie_id: kategorieId
      });
      await fetchData();
      exitSelectionMode();
    } catch (err) {
      console.error('Bulk-Kategorie-Zuweisung fehlgeschlagen:', err);
    } finally {
      setBulkAssigning(false);
    }
  }, [selectedIds, exitSelectionMode]);

  // Betrag und Saldo sind bereits korrekt aus der DB (Umbuchungen haben 2 Zeilen mit korrektem Vorzeichen)
  const accountAdjustedStatements = allStatements;

  // Available years from loaded statements
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    allStatements.forEach((stmt) => {
      if (stmt && stmt.buchungstag) {
        const year = new Date(stmt.buchungstag).getFullYear();
        if (!isNaN(year) && year >= 1900 && year <= 2100) {
          years.add(year);
        }
      }
    });
    return ['all', ...Array.from(years).sort((a, b) => b - a)];
  }, [allStatements]);

  // Filter statements client-side
  const filteredStatements = useMemo(() => {
    if (!Array.isArray(accountAdjustedStatements)) return [];

    let filtered = accountAdjustedStatements.filter((stmt) => {
      if (!stmt) return false;

      // Account filter
      if (selectedAccount !== 'all') {
        const accId = parseInt(selectedAccount);
        if (stmt.konto_id !== accId) return false;
      }

      // Year filter
      if (selectedYear !== 'all') {
        const stmtYear = new Date(stmt.buchungstag).getFullYear();
        if (stmtYear !== parseInt(selectedYear)) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && stmt.originalId !== 'start' && !(stmt.von_konto_id && stmt.nach_konto_id)) {
        const categorizedAmount = (stmt.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
        const documentAmount = (stmt.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
        const hasCategories = stmt.categories && stmt.categories.length > 0;
        const hasDocuments = stmt.documents && stmt.documents.length > 0;
        const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(stmt.betrag)) < 0.01;
        const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(stmt.betrag)) < 0.01;

        let stmtStatusColor: 'red' | 'blue' | 'green' = 'green';
        if (!hasCategories || (!hasDocuments && stmt.betrag < 0)) {
          stmtStatusColor = 'red';
        } else if (!categoryMatches || (hasDocuments && !documentMatches)) {
          stmtStatusColor = 'blue';
        } else if (categoryMatches && documentMatches) {
          stmtStatusColor = 'green';
        }

        if (statusFilter !== stmtStatusColor) {
          return false;
        }
      }

      // Tag filter (Mehrfachauswahl: Buchung muss ALLE gewählten Tags haben)
      if (filterTags.length > 0) {
        const stmtTags = stmt.tags || [];
        if (!filterTags.every(ft => stmtTags.includes(ft))) return false;
      }

      // Client-side search filter (instant)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          (stmt.buchungstext || '').toLowerCase().includes(term) ||
          (stmt.verwendungszweck || '').toLowerCase().includes(term) ||
          (stmt.name_zahlungsbeteiligter || '').toLowerCase().includes(term) ||
          (stmt.tags || []).some(t => t.toLowerCase().includes(term));
        if (!matchesSearch) return false;
      }

      return true;
    });

    return filtered;
  }, [accountAdjustedStatements, selectedAccount, selectedYear, statusFilter, searchTerm, filterTags]);

  // Nur bei Filter-Änderungen auf Seite 1 zurücksetzen, nicht bei Daten-Updates
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccount, selectedYear, statusFilter, searchTerm, filterTags]);

  // Selection helpers (brauchen filteredStatements)
  const selectAllVisible = useCallback(() => {
    const ids = new Set<string>();
    filteredStatements.forEach(s => {
      if (s.originalId !== 'start' && !(s.von_konto_id && s.nach_konto_id) && (!s.categories || s.categories.length === 0)) {
        ids.add(s.originalId);
      }
    });
    setSelectedIds(ids);
  }, [filteredStatements]);

  const selectUncategorized = useCallback(() => {
    const ids = new Set<string>();
    filteredStatements.forEach(s => {
      if (s.originalId !== 'start' && !(s.von_konto_id && s.nach_konto_id) && (!s.categories || s.categories.length === 0)) {
        ids.add(s.originalId);
      }
    });
    setSelectedIds(ids);
  }, [filteredStatements]);

  const selectedSum = useMemo(() => {
    return filteredStatements
      .filter(s => selectedIds.has(s.originalId))
      .reduce((sum, s) => sum + s.betrag, 0);
  }, [selectedIds, filteredStatements]);

  // Saldo kommt direkt aus der Datenbank (pro Buchung korrekt berechnet)
  const getSaldo = (statement: Statement) => {
    return statement.saldo ?? 0;
  };

  // Pagination
  const totalPages = Math.ceil(filteredStatements.length / statementsPerPage);
  const startIndex = (currentPage - 1) * statementsPerPage;
  const paginatedStatements = filteredStatements.slice(startIndex, startIndex + statementsPerPage);

  // Group paginated statements by date for grouped rendering
  const groupedStatements = useMemo(() => {
    const groups: { date: string; statements: Statement[] }[] = [];
    const dateMap = new Map<string, Statement[]>();
    paginatedStatements.forEach(stmt => {
      const dateKey = stmt.buchungstag ? stmt.buchungstag.split('T')[0] : 'unknown';
      if (!dateMap.has(dateKey)) {
        const stmts: Statement[] = [];
        dateMap.set(dateKey, stmts);
        groups.push({ date: dateKey, statements: stmts });
      }
      dateMap.get(dateKey)!.push(stmt);
    });
    return groups;
  }, [paginatedStatements]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Formatting helpers
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  // Date formatting helpers for grouped view
  const formatDateFull = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateCompact = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDayNumber = (dateStr: string) => new Date(dateStr).getDate();

  const getStatusInfo = (statement: Statement): { color: 'red' | 'amber' | 'green' | 'gray' } => {
    if (statement.originalId === 'start' || (statement.von_konto_id && statement.nach_konto_id)) {
      return { color: 'gray' };
    }
    const categorizedAmount = (statement.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
    const documentAmount = (statement.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
    const hasCategories = statement.categories && statement.categories.length > 0;
    const hasDocuments = statement.documents && statement.documents.length > 0;
    const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(statement.betrag)) < 0.01;
    const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(statement.betrag)) < 0.01;
    if (!hasCategories || (!hasDocuments && statement.betrag < 0)) return { color: 'red' };
    if (!categoryMatches || (hasDocuments && !documentMatches)) return { color: 'amber' };
    return { color: 'green' };
  };

  const renderStatusBadges = (statement: Statement) => {
    if (statement.originalId === 'start' || (statement.von_konto_id && statement.nach_konto_id)) return null;
    const categorizedAmount = (statement.categories || []).reduce((sum: number, cat: any) => sum + (cat.betrag || 0), 0);
    const documentAmount = (statement.documents || []).reduce((sum: number, doc: any) => sum + (doc.invoice_amount || 0), 0);
    const hasCategories = statement.categories && statement.categories.length > 0;
    const hasDocuments = statement.documents && statement.documents.length > 0;
    const categoryMatches = hasCategories && Math.abs(categorizedAmount - Math.abs(statement.betrag)) < 0.01;
    const documentMatches = hasDocuments && documentAmount > 0 && Math.abs(documentAmount - Math.abs(statement.betrag)) < 0.01;
    const hasMember = statement.categories?.some((cat: any) => cat.member_id);
    const badges: React.ReactElement[] = [];
    if (!hasCategories) {
      badges.push(<span key="cat" className="text-[10px] px-1.5 py-0.5 rounded bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 border border-error-200 dark:border-error-700">Kategorie fehlt</span>);
    } else if (categoryMatches) {
      badges.push(<span key="cat" className="text-[10px] px-1.5 py-0.5 rounded bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-700">100% kategorisiert</span>);
    }
    if (!hasDocuments && statement.betrag < 0) {
      badges.push(<span key="doc" className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700">Dokument fehlt</span>);
    } else if (hasDocuments && documentMatches) {
      badges.push(<span key="doc" className="text-[10px] px-1.5 py-0.5 rounded bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-700">{statement.documents.length} {statement.documents.length === 1 ? 'Dokument' : 'Dokumente'}</span>);
    }
    if (categoryMatches && documentMatches) {
      badges.push(<span key="ok" className="text-[10px] px-1.5 py-0.5 rounded bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-700">Vollständig</span>);
    }
    if (hasMember) {
      const memberName = statement.categories?.filter((cat: any) => cat.member_id && cat.member_name).map((cat: any) => cat.member_name).join(', ');
      badges.push(<span key="member" className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">{memberName || 'Mitglied'}</span>);
    }
    if (badges.length === 0) return null;
    return <div className="flex flex-wrap gap-1 mt-1">{badges}</div>;
  };


  // Handle statement update from modal
  const handleStatementUpdate = (updatedStatement: any) => {
    setAllStatements((prevStatements) =>
      prevStatements.map((stmt) =>
        stmt.id === `${updatedStatement.konto_id}-${updatedStatement.id}`
          ? {
              ...stmt,
              buchungstext: updatedStatement.buchungstext,
              verwendungszweck: updatedStatement.verwendungszweck,
              betrag: typeof updatedStatement.betrag === 'string'
                ? parseFloat(updatedStatement.betrag)
                : updatedStatement.betrag,
              documents: updatedStatement.documents || [],
              categories: updatedStatement.categories || [],
              tags: updatedStatement.tags || [],
            }
          : stmt
      )
    );
  };

  // Handle statement delete from modal
  const handleStatementDelete = (statementId: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Löschen bestätigen',
      message: 'Möchten Sie diesen Kontoauszug wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        try {
          // Hier würde der API-Call zum Löschen kommen
          // await apiClient.delete(`/api/finance/statements/${statementId}`);
          
          setAllStatements((prevStatements) =>
            prevStatements.filter((stmt) => stmt.originalId !== statementId)
          );
          
          setConfirmModal({
            visible: true,
            type: 'success',
            title: 'Erfolgreich',
            message: 'Der Kontoauszug wurde erfolgreich gelöscht.',
            variant: 'single',
            onConfirm: () => {},
          });
        } catch (error) {
          setConfirmModal({
            visible: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Kontoauszug konnte nicht gelöscht werden.',
            variant: 'single',
            onConfirm: () => {},
          });
        }
      },
    });
  };


  // Stats berechnen (client-seitig aus allen geladenen Buchungen)
  const stats = useMemo(() => {
    // Basiert auf filteredStatements → berücksichtigt alle aktiven Filter
    // (Konto, Jahr, Status, Suche, Tags)
    const filtered = filteredStatements;

    // Inhalts-Filter (grenzen eine Teilmenge innerhalb der Kontobewegungen ein):
    // Tags, Status oder Suche. Konto/Jahr sind KEINE Inhalts-Filter.
    const contentFilterActive = filterTags.length > 0 || statusFilter !== 'all' || searchTerm.trim() !== '';

    // Einnahmen/Ausgaben: ohne Eröffnungsbeträge
    // "Alle Konten": Umbuchungen ausschließen (heben sich auf)
    // Einzelnes Konto: Umbuchungen einbeziehen (verändern den Saldo)
    const bookingsForStats = filtered.filter(s => {
      if (s.originalId === 'start') return false;
      if (selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id) return false;
      return true;
    });
    const income = bookingsForStats.filter(s => s.betrag > 0).reduce((sum, s) => sum + s.betrag, 0);
    const expenses = bookingsForStats.filter(s => s.betrag < 0).reduce((sum, s) => sum + Math.abs(s.betrag), 0);

    // Saldo:
    // - Ohne Inhalts-Filter: laufender Kontostand aus der jüngsten Buchung
    //   (Statements sind DESC sortiert) → tatsächlicher Kontostand.
    // - Mit Inhalts-Filter (z. B. Tags): laufender Kontostand ist sinnlos,
    //   daher Netto-Bilanz der Auswahl (Einnahmen − Ausgaben).
    let balance: number;
    if (contentFilterActive) {
      balance = income - expenses;
    } else if (selectedAccount === 'all') {
      const lastSaldoPerAccount = new Map<number, number>();
      for (const s of filtered) {
        if (!lastSaldoPerAccount.has(s.konto_id) && s.saldo != null) {
          lastSaldoPerAccount.set(s.konto_id, s.saldo);
        }
      }
      balance = Array.from(lastSaldoPerAccount.values()).reduce((sum, val) => sum + val, 0);
    } else {
      const lastStatement = filtered.find(s => s.saldo != null);
      balance = lastStatement?.saldo ?? 0;
    }

    const totalTransactions = filtered.filter(s => s.originalId !== 'start').length;
    const categorizedCount = filtered.filter(s => s.categories && s.categories.length > 0).length;
    const documentsCount = filtered.reduce((sum, s) => sum + (s.documents?.length || 0), 0);

    return { balance, income, expenses, totalTransactions, categorizedCount, documentsCount, contentFilterActive };
  }, [filteredStatements, selectedAccount, filterTags, statusFilter, searchTerm]);

  /* ------------------------------------------------------------------ *
   * Mobile Ansicht (Entwurf „Kontoauszuege Mobile")
   * ------------------------------------------------------------------ */

  /** Betrag mit Vorzeichen — der Entwurf zeigt auch bei Einnahmen ein „+". */
  const formatSigned = (amount: number) =>
    `${amount > 0 ? '+' : ''}${formatCurrency(amount)}`;

  /** Buchungen je Konto für die Zähler an den Kasse-Chips. */
  const accountCounts = useMemo(() => {
    const map = new Map<string, number>();
    allStatements.forEach((s) => {
      if (s.originalId === 'start') return;
      const key = String(s.konto_id);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allStatements]);

  /** Buchungen mit offenem Beleg/offener Kategorie in der aktuellen Auswahl. */
  const openCount = useMemo(
    () => filteredStatements.filter((s) => getStatusInfo(s).color === 'red').length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredStatements],
  );

  const activeFilterCount =
    (selectedAccount !== 'all' ? 1 : 0) +
    (selectedYear !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    filterTags.length +
    (searchTerm.trim() ? 1 : 0);

  const resetAllFilters = () => {
    setSelectedAccount('all');
    setSelectedYear('all');
    setStatusFilter('all');
    setFilterTags([]);
    setSearchTerm('');
  };

  const trefferText = `${filteredStatements.length} ${filteredStatements.length === 1 ? 'Buchung' : 'Buchungen'}`;

  if (isLoading) {
    return (
      <div className="finance-page max-w-7xl mx-auto space-y-6">
        {/* Skeleton Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-56 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        {/* Skeleton Table Rows */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="flex items-center gap-4 p-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-error-500 mx-auto mb-4" />
          <p className="text-error-600 dark:text-error-400">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-page max-w-7xl mx-auto">
      {/* Header - Variante 3: Titel links | [Suchfeld] [Buttons] rechts
          Mobil ausgeblendet: die App-Kopfleiste trägt dort bereits „Kontoauszüge · Finanzen",
          Suche/Export/Neue Buchung stehen im Entwurf in einer eigenen Aktionszeile. */}
      <header className="app-page-header finance-page-header hidden md:block mb-6">
        {/* Main Header Row */}
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          {/* Left: Title */}
          <div className="flex items-center gap-3.5">
            <span className="finance-header-icon"><DocumentTextIcon /></span>
            <div>
              <p className="finance-kicker">Finanzen · Buchungsjournal</p>
              <h1 className="finance-title">Kontoauszüge</h1>
              <p className="finance-subtitle">Prüfen, zuordnen und lückenlos dokumentieren</p>
            </div>
          </div>

          {/* Mobile: nur der Auswahlmodus — Suche, Export und „Neue Buchung"
              stehen im Entwurf in einer eigenen Aktionszeile unter dem Kopf. */}
          <div className="flex md:hidden items-center gap-2">
            {allStatements.length > 0 && (
              <button
                onClick={selectionMode ? exitSelectionMode : enterSelectionMode}
                aria-label={selectionMode ? 'Auswahl beenden' : 'Buchungen auswählen'}
                className={`h-10 px-3 rounded-xl flex items-center gap-2 transition-colors ${
                  selectionMode
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {selectionMode ? <XMarkIcon className="h-5 w-5" /> : <ListBulletIcon className="h-5 w-5" />}
              </button>
            )}
          </div>

          {/* Desktop: Actions */}
          <div className="hidden md:flex items-center gap-2 md:gap-4 flex-shrink-0">
            {allStatements.length > 0 && (
              <>
                {/* Desktop Search Field */}
                <div className="hidden 2xl:block relative w-[280px]">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buchungen suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-11 pl-11 pr-4 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <button
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className={`hidden md:flex 2xl:hidden w-11 h-11 items-center justify-center rounded-xl transition-colors ${
                    showMobileSearch
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  title="Buchungen suchen"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>

                <button
                  onClick={() => setShowImportModal(true)}
                  className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="CSV Import"
                >
                  <DocumentIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Excel Export"
                >
                  <DocumentArrowDownIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
              </>
            )}
            {selectionMode ? (
              <button
                onClick={exitSelectionMode}
                className="h-11 px-4 flex items-center gap-2 rounded-xl text-sm font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
                <span>Abbrechen</span>
              </button>
            ) : (
              <>
                <button
                  onClick={enterSelectionMode}
                  className="h-11 px-4 flex items-center gap-2 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <ListBulletIcon className="h-5 w-5" />
                  <span>Auswählen</span>
                </button>
                <button
                  onClick={() => setShowAddBookingModal(true)}
                  className="h-11 px-4 flex items-center gap-2 rounded-xl text-white text-sm font-semibold bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Neue Buchung</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Search Row - Expandable */}
        {showMobileSearch && allStatements.length > 0 && (
          <div className="hidden md:block 2xl:hidden px-4 pb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buchungen suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                className="w-full h-11 pl-11 pr-4 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </header>

      {/* ---------------- Mobil: Saldo + Aktionszeile ---------------- */}
      {allStatements.length > 0 && (
        <div className="md:hidden mb-4 mt-1 space-y-3">
          {/* Saldo-Karte mit Einnahmen / Ausgaben / Offen */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
            <div className="px-4 pb-3.5 pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                {stats.contentFilterActive ? 'Bilanz · aktuelle Auswahl' : 'Gesamtsaldo · alle Kassen'}
              </p>
              <p
                className={`mt-1 text-[30px] font-bold leading-none tracking-tight tabular-nums ${
                  stats.balance >= 0 ? 'text-gray-900 dark:text-white' : 'text-error-600 dark:text-error-400'
                }`}
              >
                {formatCurrency(stats.balance)}
              </p>
            </div>
            <div className="finance-mobile-stat-grid grid grid-cols-3 border-t border-gray-200 dark:border-gray-700">
              <div className="finance-mobile-stat-cell flex min-w-0 flex-col gap-0.5 px-3.5 py-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                  Einnahmen
                </span>
                <span className="finance-mobile-stat-value whitespace-nowrap text-[15px] font-semibold tabular-nums text-success-600 dark:text-success-400">
                  {formatCurrency(stats.income)}
                </span>
              </div>
              <div className="finance-mobile-stat-cell flex min-w-0 flex-col gap-0.5 border-l border-gray-200 px-3.5 py-3 dark:border-gray-700">
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                  Ausgaben
                </span>
                <span className="finance-mobile-stat-value whitespace-nowrap text-[15px] font-semibold tabular-nums text-error-600 dark:text-error-400">
                  {formatCurrency(stats.expenses)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStatusFilter(statusFilter === 'red' ? 'all' : 'red');
                  setFiltersOpen(true);
                }}
                className={`finance-mobile-stat-cell flex min-w-0 flex-col gap-0.5 border-l border-gray-200 px-3.5 py-3 text-left transition-colors dark:border-gray-700 ${
                  statusFilter === 'red' ? 'bg-error-50 dark:bg-error-900/20' : ''
                }`}
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                  Offen
                </span>
                <span className="finance-mobile-stat-value inline-flex items-center gap-1.5 whitespace-nowrap text-[15px] font-semibold tabular-nums text-error-600 dark:text-error-400">
                  <span className="h-[7px] w-[7px] rounded-full bg-error-500" />
                  {openCount}
                </span>
              </button>
            </div>
            <p className="flex items-center gap-1.5 border-t border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
              <FunnelIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0">Einnahmen/Ausgaben zur aktuellen Auswahl · {trefferText}</span>
            </p>
          </div>

          {/* Suche + Export + Neue Buchung */}
          <div className="flex items-center gap-2">
            <div className="flex h-[42px] min-w-0 flex-1 items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 dark:border-gray-600 dark:bg-[#1c2432]">
              <MagnifyingGlassIcon className="h-[17px] w-[17px] flex-shrink-0 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Suchen …"
                className="min-w-0 flex-1 border-none bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Suche leeren"
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-gray-400"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={selectionMode ? exitSelectionMode : enterSelectionMode}
              aria-label={selectionMode ? 'Auswahl beenden' : 'Buchungen auswählen'}
              className={`flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl border transition-colors ${
                selectionMode
                  ? 'border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-300'
              }`}
            >
              {selectionMode ? <XMarkIcon className="h-[19px] w-[19px]" /> : <ListBulletIcon className="h-[19px] w-[19px]" />}
            </button>
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              aria-label="Excel-Export"
              className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-300"
            >
              <DocumentArrowDownIcon className="h-[19px] w-[19px]" />
            </button>
            <button
              type="button"
              onClick={() => setShowAddBookingModal(true)}
              aria-label="Neue Buchung erfassen"
              className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>

        </div>
      )}

      {/* Stats Cards - 3 Column (ab md; mobil ersetzt durch die Saldo-Karte) */}
      {allStatements.length > 0 && (
        <StatGroup columns={3} className="hidden md:block mb-4 sm:mb-6">
          <StatCard
            icon={BanknotesIcon}
            tone="neutral"
            valueTone={stats.balance >= 0 ? 'neutral' : 'error'}
            label={stats.contentFilterActive ? 'Bilanz (Auswahl)' : 'Gesamtsaldo'}
            value={formatCurrency(stats.balance)}
          />
          <StatCard
            icon={ArrowTrendingUpIcon}
            tone="neutral"
            valueTone="success"
            label="Einnahmen"
            value={formatCurrency(stats.income)}
          />
          <StatCard
            icon={ArrowTrendingDownIcon}
            tone="neutral"
            valueTone="error"
            label="Ausgaben"
            value={formatCurrency(stats.expenses)}
          />
        </StatGroup>
      )}

      {/* Filter — Entwurf „Kontoauszuege Mobile", auf allen Breiten */}
      {allStatements.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432] sm:mb-6">
          <button
            type="button"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-expanded={filtersOpen}
            className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left md:px-5 md:py-3.5"
          >
            <AdjustmentsHorizontalIcon className="h-[17px] w-[17px] flex-shrink-0 text-gray-500 dark:text-gray-400" />
            <span className="flex-1 text-sm font-semibold text-gray-900 dark:text-white">Filter</span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                activeFilterCount
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {activeFilterCount ? `${activeFilterCount} aktiv` : 'keine'}
            </span>
            {filtersOpen ? (
              <ChevronUpIcon className="h-[17px] w-[17px] flex-shrink-0 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-[17px] w-[17px] flex-shrink-0 text-gray-400" />
            )}
          </button>

          {filtersOpen && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              {[
                {
                  label: 'Kasse',
                  chips: accounts.map((account) => ({
                    key: String(account.id),
                    label: account.id === 'all' ? 'Alle' : account.name,
                    active: selectedAccount === String(account.id),
                    count: account.id === 'all' ? undefined : accountCounts.get(String(account.id)) || 0,
                    onClick: () => setSelectedAccount(String(account.id)),
                  })),
                },
                {
                  label: 'Zeitraum',
                  chips: availableYears.map((year) => ({
                    key: String(year),
                    label: year === 'all' ? 'Alle Jahre' : String(year),
                    active: selectedYear === String(year),
                    count: undefined,
                    onClick: () => setSelectedYear(String(year)),
                  })),
                },
                {
                  label: 'Belegstatus',
                  chips: [
                    { key: 'all', label: 'Alle', active: statusFilter === 'all', dot: undefined, onClick: () => setStatusFilter('all') },
                    { key: 'red', label: 'Fehlt', active: statusFilter === 'red', dot: 'bg-error-500', onClick: () => setStatusFilter('red') },
                    { key: 'blue', label: 'Teil', active: statusFilter === 'blue', dot: 'bg-info-500', onClick: () => setStatusFilter('blue') },
                    { key: 'green', label: 'OK', active: statusFilter === 'green', dot: 'bg-success-500', onClick: () => setStatusFilter('green') },
                  ].map((c) => ({ ...c, count: undefined })),
                },
                ...(availableBuchungTags.length > 0
                  ? [
                      {
                        label: 'Projekte & Tags',
                        chips: availableBuchungTags.map(({ tag, count }) => ({
                          key: tag,
                          label: tag,
                          active: filterTags.includes(tag),
                          count,
                          onClick: () =>
                            setFilterTags((prev) =>
                              prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
                            ),
                        })),
                      },
                    ]
                  : []),
              ].map((facet) => (
                <div key={facet.label} className="border-b border-gray-200 py-3 dark:border-gray-700 md:py-3.5">
                  <div className="flex items-center gap-2 px-3.5 md:px-5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                      {facet.label}
                    </span>
                    <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                  </div>
                  {/* Mobil scrollt die Chip-Reihe seitlich, ab md bricht sie um. */}
                  <div className="mt-2 flex gap-2 overflow-x-auto px-3.5 pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:gap-2.5 md:overflow-x-visible md:px-5">
                    {facet.chips.map((chip: any) => (
                      <FilterChip
                        key={chip.key}
                        label={chip.label}
                        active={chip.active}
                        dot={chip.dot}
                        count={chip.count}
                        onClick={chip.onClick}
                      />
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 bg-gray-50 px-3.5 py-2.5 dark:bg-gray-900/30 md:px-5 md:py-3">
                <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
                  {trefferText} · Summe {formatSigned(stats.income - stats.expenses)}
                </span>
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className="flex-none text-xs font-semibold text-primary-600 dark:text-primary-400"
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statements List or Welcome Screen */}
      {allStatements.length === 0 && !isLoading ? (
        <div className="bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 p-8 sm:p-12">
          <div className="max-w-2xl mx-auto text-center">
            <BanknotesIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Willkommen bei den Kontoauszügen
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Hier können Sie alle Ihre Kontobewegungen übersichtlich verwalten und analysieren.
            </p>

            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-8 mb-8 text-left border border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100 mb-6 flex items-center gap-2">
                <DocumentTextIcon className="h-6 w-6" />
                So nutzen Sie die Kontoauszüge:
              </h3>
              <div className="space-y-4">
                <div className="flex gap-4 bg-white dark:bg-[#1c2432] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <DocumentIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      CSV-Dateien importieren
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Laden Sie Ihre Kontoauszüge als CSV-Datei von Ihrer Bank herunter und importieren Sie diese hier.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      <strong>Unterstützte Banken:</strong> Sparkasse, Commerzbank, DKB und weitere
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-[#1c2432] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <PlusIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Manuelle Buchungen erfassen
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Erfassen Sie Bargeldtransaktionen oder andere Buchungen manuell über den "Neue Buchung" Button.
                    </p>
                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-md p-2 border border-primary-200 dark:border-primary-700/30 mt-2">
                      <ul className="text-xs text-primary-700 dark:text-primary-300 space-y-1">
                        <li>• Ein- und Auszahlungen</li>
                        <li>• Umbuchungen zwischen Konten</li>
                        <li>• Kategorisierung möglich</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-[#1c2432] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <TagIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Kategorien zuordnen
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Ordnen Sie Buchungen Kategorien zu für eine bessere Übersicht über Ihre Finanzen.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      <strong>Tipp:</strong> Die KI kann Kategorien automatisch vorschlagen
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-[#1c2432] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    4
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <DocumentArrowDownIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Excel-Export
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Exportieren Sie Ihre Kontoauszüge als Excel-Datei für weitere Analysen oder die Steuererklärung.
                    </p>
                    <div className="bg-success-50 dark:bg-success-900/20 rounded-md p-2 border border-success-200 dark:border-success-700/30 mt-2">
                      <ul className="text-xs text-success-700 dark:text-success-300 space-y-1">
                        <li>• Filterung nach Jahr und Konto</li>
                        <li>• Inklusive Kategorien und Notizen</li>
                        <li>• Bereit für Steuerberater</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
              >
                <DocumentIcon className="h-5 w-5" />
                CSV-Datei importieren
              </button>
              <button
                onClick={() => setShowAddBookingModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                <PlusIcon className="h-5 w-5" />
                Erste Buchung erfassen
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="space-y-6">
        {groupedStatements.length > 0 ? (
          groupedStatements.map((group, groupIndex) => {
            const dayIncome = group.statements
              .filter(s => s.betrag > 0 && s.originalId !== 'start' && !(selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id))
              .reduce((sum, s) => sum + s.betrag, 0);
            const dayExpenses = group.statements
              .filter(s => s.betrag < 0 && s.originalId !== 'start' && !(selectedAccount === 'all' && s.von_konto_id && s.nach_konto_id))
              .reduce((sum, s) => sum + Math.abs(s.betrag), 0);
            const dayNet = dayIncome - dayExpenses;
            const dayCount = group.statements.filter(s => s.originalId !== 'start').length;

            // Year divider: check if year changed from previous group
            const currentYear = new Date(group.date).getFullYear();
            const prevYear = groupIndex > 0 ? new Date(groupedStatements[groupIndex - 1].date).getFullYear() : currentYear;
            const showYearDivider = groupIndex > 0 && currentYear !== prevYear;

            return (
              <div key={group.date}>
                {/* Year Divider with Saldo */}
                {showYearDivider && (() => {
                  // Letzter Saldo des abgeschlossenen Jahres (currentYear, da DESC: 2026 oben, 2025 unten → Divider zeigt Abschluss von 2025)
                  const lastStmtOfYear = filteredStatements.find(s =>
                    s.saldo != null && new Date(s.buchungstag).getFullYear() === currentYear
                  );
                  const yearEndSaldo = lastStmtOfYear?.saldo ?? 0;

                  return (
                    <div className="flex items-center gap-4 mb-6 -mt-1">
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-500 dark:text-gray-400 tracking-wide">Jahresabschluss {currentYear}</span>
                        <span className={`text-sm font-bold tabular-nums ${yearEndSaldo >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                          {formatCurrency(yearEndSaldo)}
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                    </div>
                  );
                })()}

                {/* Date Group Header */}
                <div className="flex items-baseline justify-between gap-3 mb-2 md:mb-3 md:items-center">
                  <div className="flex items-center gap-3">
                    <div className="hidden md:flex w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{getDayNumber(group.date)}</span>
                    </div>
                    <div>
                      <p className="hidden md:block text-sm font-semibold text-gray-900 dark:text-white">{formatDateFull(group.date)}</p>
                      <p className="md:hidden px-0.5 text-sm font-bold tracking-tight text-gray-700 dark:text-gray-300">{formatDateCompact(group.date)}</p>
                      <p className="hidden md:block text-xs text-gray-400 dark:text-gray-500">{dayCount} {dayCount === 1 ? 'Transaktion' : 'Transaktionen'}</p>
                    </div>
                  </div>
                  {/* Mobil: schlichtes Versalien-Label mit Wert statt Pille */}
                  <span className="md:hidden inline-flex flex-none items-baseline gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500">
                    Tagessaldo
                    <span className={`text-xs font-semibold normal-case tracking-normal tabular-nums ${
                      dayNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'
                    }`}>
                      {formatSigned(dayNet)}
                    </span>
                  </span>
                  <div className="hidden md:flex items-center text-xs">
                    <span className={`px-2 py-1 rounded-md font-semibold text-[11px] sm:text-xs ${
                      dayNet >= 0
                        ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400'
                        : 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-400'
                    }`}>
                      Tagessaldo: {dayNet >= 0 ? '+' : ''}{formatCurrency(dayNet)}
                    </span>
                  </div>
                </div>

                {/* Desktop: Table-like rows inside white card */}
                <div className="hidden md:block bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {group.statements.map(statement => {
                      const status = getStatusInfo(statement);
                      const isNormalBooking = statement.originalId !== 'start' && !(statement.von_konto_id && statement.nach_konto_id);
                      const hasCategories = statement.categories && statement.categories.length > 0;
                      const isSelectable = isNormalBooking && !hasCategories;
                      const isSelected = selectedIds.has(statement.originalId);
                      const isComplete = status.color === 'green';

                      return (
                        <div
                          key={statement.id}
                          onClick={() => {
                            if (selectionMode && isSelectable) toggleSelection(statement.originalId);
                            else if (!selectionMode) setSelectedStatementId(statement.originalId);
                          }}
                          className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                            selectionMode && isSelected
                              ? 'bg-primary-50 dark:bg-primary-900/20'
                              : selectionMode && !isSelectable
                              ? 'opacity-40 cursor-not-allowed'
                              : isComplete
                              ? 'bg-success-50/30 dark:bg-success-900/10 hover:bg-success-50/50 dark:hover:bg-success-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                          }`}
                        >
                          {/* Selection checkbox */}
                          {selectionMode && isSelectable && (
                            <div className={`flex-shrink-0 w-5 h-5 mr-3 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                            </div>
                          )}

                          {/* Status icon */}
                          <div className="w-8 flex-shrink-0">
                            {status.color === 'green' && (
                              <span className="inline-flex w-6 h-6 rounded-full bg-success-50 dark:bg-success-900/30 items-center justify-center">
                                <CheckIcon className="w-3 h-3 text-success-500" />
                              </span>
                            )}
                            {status.color === 'amber' && (
                              <span className="inline-flex w-6 h-6 rounded-full bg-amber-50 dark:bg-amber-900/30 items-center justify-center">
                                <ExclamationTriangleIcon className="w-3 h-3 text-amber-500" />
                              </span>
                            )}
                            {status.color === 'red' && (
                              <span className="inline-flex w-6 h-6 rounded-full bg-error-50 dark:bg-error-900/30 items-center justify-center">
                                <XMarkIcon className="w-3 h-3 text-error-500" />
                              </span>
                            )}
                            {status.color === 'gray' && (
                              <span className="inline-flex w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 items-center justify-center">
                                <ArrowsRightLeftIcon className="w-3 h-3 text-gray-400" />
                              </span>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 ml-3">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {statement.name_zahlungsbeteiligter || statement.buchungstext}
                              </p>
                              {statement.von_konto_id && statement.nach_konto_id && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-700 flex-shrink-0">Umbuchung</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {statement.verwendungszweck || 'Kein Verwendungszweck'}
                            </p>
                            <div className="flex items-center gap-1 flex-wrap">
                              {renderStatusBadges(statement)}
                              {statement.tags && statement.tags.map(tag => (
                                <span key={tag} className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded-full">
                                  <TagIcon className="w-2.5 h-2.5" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Account */}
                          <span className="hidden lg:inline text-xs text-gray-400 dark:text-gray-500 mx-3 flex-shrink-0">
                            {accounts.find(a => a.id === statement.konto_id)?.name}
                          </span>

                          {/* Amount */}
                          <p className={`text-sm font-semibold w-24 text-right flex-shrink-0 tabular-nums ${
                            statement.betrag < 0 ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'
                          }`}>
                            {formatCurrency(statement.betrag)}
                          </p>

                          {/* Balance */}
                          <p className="text-xs text-gray-400 dark:text-gray-500 w-24 text-right flex-shrink-0 tabular-nums">
                            {formatCurrency(getSaldo(statement))}
                          </p>

                          {/* Chevron */}
                          <ChevronDownIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 ml-3 flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Mobile/Tablet: eine Karte je Tag, Buchungen als Zeilen */}
                <div className="md:hidden overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
                  {group.statements.map((statement, rowIndex) => {
                    const status = getStatusInfo(statement);
                    const meta = STATUS_META[status.color];
                    const isNormalBooking = statement.originalId !== 'start' && !(statement.von_konto_id && statement.nach_konto_id);
                    const hasCategories = statement.categories && statement.categories.length > 0;
                    const isSelectable = isNormalBooking && !hasCategories;
                    const isSelected = selectedIds.has(statement.originalId);
                    const kontoName = accounts.find(a => a.id === statement.konto_id)?.name;
                    const { Icon: KontoIcon, tile } = getKontoVisual(kontoName);

                    return (
                      <div
                        key={statement.id}
                        onClick={() => {
                          if (selectionMode && isSelectable) toggleSelection(statement.originalId);
                          else if (!selectionMode) setSelectedStatementId(statement.originalId);
                        }}
                        className={`flex cursor-pointer items-center gap-3 px-3.5 py-3 transition-colors ${
                          rowIndex > 0 ? 'border-t border-gray-100 dark:border-gray-700/60' : ''
                        } ${
                          selectionMode && isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        } ${selectionMode && !isSelectable ? 'cursor-not-allowed opacity-40' : ''}`}
                      >
                        {selectionMode && isSelectable && (
                          <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                            isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                          </div>
                        )}

                        <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl ${tile}`}>
                          <KontoIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {statement.name_zahlungsbeteiligter || statement.buchungstext}
                          </p>
                          <p className="truncate text-[13px] text-gray-600 dark:text-gray-300">
                            {statement.verwendungszweck || 'Kein Verwendungszweck'}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">{kontoName}</span>
                            <span className="h-[3px] w-[3px] flex-none rounded-full bg-gray-300 dark:bg-gray-600" />
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${meta.text}`}>
                              <span className={`h-1.5 w-1.5 flex-none rounded-full ${meta.dot}`} />
                              {meta.label}
                            </span>
                            {statement.tags && statement.tags.map(tag => (
                              <span
                                key={tag}
                                className="max-w-[130px] truncate rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-none flex-col items-end gap-0.5">
                          <span className={`text-sm font-semibold tabular-nums ${
                            statement.betrag < 0 ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'
                          }`}>
                            {formatSigned(statement.betrag)}
                          </span>
                          <span className="whitespace-nowrap text-[11px] tabular-nums text-gray-400 dark:text-gray-500">
                            Saldo {formatCurrency(getSaldo(statement))}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center dark:border-gray-600 dark:bg-[#1c2432]">
            <DocumentMagnifyingGlassIcon className="h-7 w-7 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <p className="text-base font-semibold text-gray-900 dark:text-white">Keine Buchungen gefunden</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Passe die Filter an oder setze sie zurück.</p>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetAllFilters}
                className="mt-1 text-sm font-semibold text-primary-600 dark:text-primary-400"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
            {startIndex + 1}-{Math.min(startIndex + statementsPerPage, filteredStatements.length)} von {filteredStatements.length}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg border ${
                currentPage === 1
                  ? 'border-gray-300 dark:border-gray-600 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = currentPage - 2 + i;
              if (pageNum < 1 || pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-2 sm:px-3 py-1 rounded-lg text-sm ${
                    currentPage === pageNum
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            }).filter(Boolean)}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg border ${
                currentPage === totalPages
                  ? 'border-gray-300 dark:border-gray-600 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk Selection Action Bar */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 sm:sticky sm:bottom-4 sm:mt-4 sm:rounded-xl">
          <div className="bg-white dark:bg-gray-800 border-t sm:border border-gray-200 dark:border-gray-700 shadow-lg sm:rounded-xl p-4 animate-slide-up">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-3">
              {/* Info */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30">
                  <span className="text-sm font-bold text-primary-700 dark:text-primary-300">{selectedIds.size}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">ausgewählt</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-2">({formatCurrency(selectedSum)})</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-1 w-full sm:w-auto sm:justify-end flex-wrap">
                <button
                  onClick={selectAllVisible}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Alle
                </button>
                <button
                  onClick={selectUncategorized}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Ohne Kategorie
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Keine
                </button>

                {/* Sammelzuweisung — Combobox statt aufklappender Liste:
                    die alte hing als `absolute` in der Leiste und wurde von
                    deren Vorfahren abgeschnitten. */}
                <div className="w-56">
                  <Combobox
                    label="Kategorie zuweisen"
                    placeholder={bulkAssigning ? 'Wird zugewiesen …' : 'Kategorie zuweisen'}
                    value={null}
                    disabled={bulkAssigning}
                    options={sortedBulkCategories.map((c: any) => ({ id: c.id, label: c.name }))}
                    onChange={(id) => handleBulkCategoryAssign(Number(id))}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedStatementId && (
        <StatementDetailModal
          statementId={selectedStatementId}
          onClose={() => setSelectedStatementId(null)}
          onStatementUpdate={handleStatementUpdate}
          onStatementDelete={handleStatementDelete}
        />
      )}

      {/* Excel Export Modal */}
      <ExcelExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        accounts={accounts}
        allStatements={filteredStatements}
        isMobile={window.innerWidth < 640}
      />

      {/* CSV Import Modal */}
      {showImportModal && (
        <CSVImporter
          onImportSuccess={() => {
            setShowImportModal(false);
            fetchData(); // Reload data after import
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* Add Booking Modal */}
      <AddBookingModal
        isOpen={showAddBookingModal}
        onClose={() => setShowAddBookingModal(false)}
        onSuccess={() => {
          fetchData(); // Reload data after successful booking
        }}
      />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default AccountStatements;
